import config from '../config'
import React, { useState, useEffect, createContext, useContext } from 'react'
import { useLocation } from 'react-router-dom'

// utilities
import { guardedRequest } from '../utils'

// contexts
import { useAppState } from './AppStateContext'
import { useUserState } from './UserContext'

const ClientContext = createContext()

const fetchClient = (config, setGlobalProcessing, accessToken, location, setClient, setAppInitialized, setGlobalError) => async (prod, isCancelled) => {
  if (setGlobalProcessing) {
    setGlobalProcessing(true)
    if (accessToken) {
      const apiUrl = prod ? config.prodApiUrl : config.apiUrl
      try {
        const clients = await guardedRequest(location, {
          url: `${apiUrl}/clients`,
          auth: { bearer: accessToken },
          method: 'get',
          json: true
        })
        if (isCancelled) { return }
        if (prod && clients && clients[0]) {
          clients[0].prod = true
        }
        if (clients.length) setClient(clients[0])
        else setClient(undefined)
        setAppInitialized(true)
      } catch (error) {
        if (error.message !== 'Unauthorized') setGlobalError(true)
        console.log(error)
      }
      setGlobalProcessing(false)
    }
  }
}

const ClientProvider = ({ children }) => {
  const location = useLocation()
  const { accessToken } = useUserState()
  const [client, setClient] = useState()
  const [logs, setLogs] = useState()
  const [filter, setFilter] = useState({ alarmId: '' })
  const [filteredLogs, setFilteredLogs] = useState()
  const { setGlobalProcessing, setAppInitialized, setGlobalError } = useAppState()

  useEffect(() => {
    let isCancelled = false

    async function runEffect () {
      return fetchClient(config, setGlobalProcessing, accessToken, location, setClient, setAppInitialized, setGlobalError)(false, isCancelled)
    }

    function cancelEffect () {
      isCancelled = true
    }

    runEffect()
    return cancelEffect
  }, [accessToken, setAppInitialized, setGlobalError, setGlobalProcessing])

  const createClient = async (name, desc, prod, verify, dispatch) => {
    const apiUrl = prod ? config.prodApiUrl : config.apiUrl
    const apis = []
    if (verify) apis.push('verify')
    if (dispatch) apis.push('dispatch')
    const data = await guardedRequest(location, {
      url: `${apiUrl}/clients`,
      auth: { bearer: accessToken },
      method: 'post',
      json: true,
      body: {
        display_name: name,
        description: desc,
        apis
      }
    })
    setClient(data)
  }

  const updateWebhook = async (type, url, prod) => {
    const apiUrl = prod ? config.prodApiUrl : config.apiUrl
    const options = {
      url: `${apiUrl}/clients/${client.client_id}/webhooks`,
      auth: { bearer: accessToken },
      method: 'put',
      json: true,
      body: {
        type,
        url
      }
    }
    const { secret } = await guardedRequest(location, options)
    if (!client.webhooks.find(w => w.type === type)) {
      client.webhooks.push({
        type,
        url,
        secret
      })
    }
    setClient(client => Object.assign({}, client, {
      webhooks: client.webhooks.map(webhook => webhook.type === type ? { type, url, secret } : webhook)
    }))
  }

  const fetchLogs = async (abort) => {
    const data = await guardedRequest(location, {
      url: `${config.apiUrl}/clients/${client.client_id}/logs`,
      auth: { bearer: accessToken },
      method: 'get',
      json: true
    })
    if (abort) { return }
    setLogs(data)
  }

  const fetchFilteredLogs = async (abort) => {
    if (!filter.alarmId) { return }
    const alarmId = filter.alarmId
    const data = await guardedRequest(location, {
      url: `${config.apiUrl}/clients/${client.client_id}/logs?alarmId=${alarmId}`,
      auth: { bearer: accessToken },
      method: 'get',
      json: true
    })
    if (abort) { return }
    if (alarmId === filter.alarmId) setFilteredLogs(data)
  }

  const simulateEvent = async (alarmId, eventType) => {
    await guardedRequest(location, {
      url: `${config.apiUrl}/clients/${client.client_id}/alarms/${alarmId}/events/test`,
      auth: { bearer: accessToken },
      method: 'post',
      json: true,
      body: { event_type: eventType }
    })
  }

  const liftedFetchClient = fetchClient(config, setGlobalProcessing, accessToken, location, setClient, setAppInitialized, setGlobalError)

  return (
    <ClientContext.Provider value={{
      client,
      createClient,
      logs,
      filter,
      setFilter,
      filteredLogs,
      setFilteredLogs,
      fetchLogs,
      fetchFilteredLogs,
      simulateEvent,
      updateWebhook,
      fetchClient: liftedFetchClient
    }}>
      {children}
    </ClientContext.Provider>
  )
}

const useClient = () => useContext(ClientContext)

export { ClientProvider, useClient }
